import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { CheckboxIcon, CheckboxOutlineIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useStyles } from "./styles";

const CHECKBOX_SIZE = 24;

const cardCheckboxControl = {
  icon: <CheckboxOutlineIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />,
  checkedIcon: <CheckboxIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />,
  sx: {
    p: 0,
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
    color: "text.secondary",
    "&.Mui-checked": {
      color: "primary.main",
    },
  },
};

export type FormOptionCardFieldKey =
  | "note" | "quantity" | "size" | "radio" | "location" | "itemType" | "subCheckbox";

export type FormOptionCardRadioOption = { value: string; labelKey: string };

export type FormOptionCardValue = {
  enabled: boolean;
  selectedOption: string;
  note: string;
  quantity: string;
  size: string;
  location: string;
  itemType: string;
  optionEnabled: boolean;
};

export type FormOptionCardConfig = {
  titleKey: string;
  layout?: "vertical" | "horizontal";
  fields: FormOptionCardFieldKey[];
  radioLabelKey?: string;
  radioOptions?: FormOptionCardRadioOption[];
  radioModalTriggerOptions?: string[];
  subCheckboxLabelKey?: string;
  notePlaceholderKey?: string;
  locationPlaceholderKey?: string;
  quantityLabelKey?: string;
  sizeLabelKey?: string;
};

export type FormOptionCardLabels = Record<
  "note" | "quantity" | "size" | "location" | "itemType",
  string
>;

export type FormOptionCardProps = {
  config: FormOptionCardConfig;
  value: FormOptionCardValue;
  labels: FormOptionCardLabels;
  onChange: (value: Partial<FormOptionCardValue>) => void;
  onRadioModalTrigger?: () => void;
};

const I18N_PREFIXES = ["WORK_ORDER.", "QUOTES."] as const;

const resolveValue = (fieldValue: string, t: (key: string) => string) =>
  I18N_PREFIXES.some((prefix) => fieldValue.startsWith(prefix)) ? t(fieldValue) : fieldValue;

type TextFieldDef = {
  labelKey: keyof FormOptionCardLabels;
  valueKey: keyof FormOptionCardValue;
  inputClassKey: "noteInput" | "numberInput";
  layout?: "flex" | "narrow";
  type?: "number";
  resolve?: boolean;
  placeholderFromConfig?: boolean;
};

const TEXT_FIELDS: Record<
  Exclude<FormOptionCardFieldKey, "radio" | "subCheckbox">,
  TextFieldDef
> = {
  note: { labelKey: "note", valueKey: "note", inputClassKey: "noteInput", layout: "flex", resolve: true, placeholderFromConfig: true },
  quantity: { labelKey: "quantity", valueKey: "quantity", inputClassKey: "numberInput", layout: "narrow", type: "number" },
  size: { labelKey: "size", valueKey: "size", inputClassKey: "numberInput", layout: "narrow", type: "number" },
  location: { labelKey: "location", valueKey: "location", inputClassKey: "noteInput", layout: "flex", resolve: true, placeholderFromConfig: true },
  itemType: { labelKey: "itemType", valueKey: "itemType", inputClassKey: "noteInput", layout: "flex", resolve: true },
};

export const FormOptionCard = ({
  config,
  value,
  labels,
  onChange,
  onRadioModalTrigger,
}: FormOptionCardProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const isHorizontal = config.layout === "horizontal";
  const groupClass = (layout?: "flex" | "narrow") =>
    [styles.fieldGroup, isHorizontal && layout === "flex" && styles.fieldFlex, isHorizontal && layout === "narrow" && styles.fieldNarrow]
      .filter(Boolean)
      .join(" ");

  const triggerModalIfNeeded = (optionValue: string) => {
    if (config.radioModalTriggerOptions?.includes(optionValue)) {
      onRadioModalTrigger?.();
    }
  };

  const renderField = (fieldKey: FormOptionCardFieldKey) => {
    if (fieldKey === "radio" && config.radioOptions) {
      return (
        <Box key="radio" className={`${styles.fieldGroup} ${isHorizontal ? styles.radioField : ""}`}>
          {config.radioLabelKey && (
            <Typography className={styles.fieldLabel}>{t(config.radioLabelKey)}</Typography>
          )}
          <RadioGroup
            className={styles.radioGroup}
            row
            value={value.selectedOption}
            onChange={(event) => {
              const nextValue = event.target.value;
              onChange({ selectedOption: nextValue });
              triggerModalIfNeeded(nextValue);
            }}
          >
            {config.radioOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                className={styles.radioOptionCard}
                value={option.value}
                control={<Radio size="small" sx={{ p: 0.5, flexShrink: 0 }} />}
                label={t(option.labelKey)}
                labelPlacement="end"
                onMouseDown={() => {
                  if (option.value === value.selectedOption) {
                    triggerModalIfNeeded(option.value);
                  }
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      );
    }

    if (fieldKey === "subCheckbox" && config.subCheckboxLabelKey) {
      return (
        <Box key="subCheckbox" className={styles.subCheckboxRow}>
          <Checkbox
            {...cardCheckboxControl}
            checked={value.optionEnabled}
            onChange={(event) => onChange({ optionEnabled: event.target.checked })}
          />
          <Typography className={styles.subCheckboxLabel}>{t(config.subCheckboxLabelKey)}</Typography>
        </Box>
      );
    }

    const field = TEXT_FIELDS[fieldKey as keyof typeof TEXT_FIELDS];
    if (!field) {
      return null;
    }

    const rawValue = value[field.valueKey] as string;
    const placeholderKey = field.placeholderFromConfig
      ? fieldKey === "location"
        ? config.locationPlaceholderKey ?? config.notePlaceholderKey ?? labels.note
        : config.notePlaceholderKey ?? labels.note
      : undefined;

    const labelKey =
      fieldKey === "quantity" && config.quantityLabelKey
        ? config.quantityLabelKey
        : fieldKey === "size" && config.sizeLabelKey
          ? config.sizeLabelKey
          : labels[field.labelKey];

    return (
      <Box key={fieldKey} className={groupClass(field.layout)}>
        <Typography className={styles.fieldLabel}>{t(labelKey)}</Typography>
        <TextField
          className={styles[field.inputClassKey]}
          fullWidth
          variant="outlined"
          type={field.type}
          placeholder={placeholderKey ? t(placeholderKey) : undefined}
          value={field.resolve ? resolveValue(rawValue, t) : rawValue}
          onChange={(event) => onChange({ [field.valueKey]: event.target.value })}
        />
      </Box>
    );
  };

  return (
    <Box className={`${styles.card} ${value.enabled ? styles.cardActive : ""}`}>
      <Box className={styles.cardHeader}>
        <Checkbox
          {...cardCheckboxControl}
          checked={value.enabled}
          onChange={(event) => onChange({ enabled: event.target.checked })}
        />
        <Typography className={styles.cardTitle}>{t(config.titleKey)}</Typography>
      </Box>
      <Box className={styles.cardDivider} />
      <Box className={isHorizontal ? styles.fieldsRow : styles.fieldsColumn}>
        {config.fields.map(renderField)}
      </Box>
    </Box>
  );
};

export default FormOptionCard;
